/** Așezarea mobilierului în cameră — PROVIZORIU, în browser.
 *
 *  ==========================================================================
 *  FIȘIERUL ĂSTA E DE ȘTERS. Nu într-un sens moral: e scris ca să fie șters.
 *
 *  Verdictul cercetării de pe 31.08.2026 e că așezarea mobilierului ține de
 *  ETAPA DE ARHITECTURĂ, în Python, cu dreptunghiul interior maxim și baleiajul
 *  ușii scăzut cu `shapely` — nu de randor. Motivul e că mobilierul e o DECIZIE
 *  de proiectare, nu o convenție de desen: trebuie să iasă la fel în planșă, în
 *  machetă și în antemăsurătoare, deci trebuie să stea în document, unde toate
 *  trei îl citesc. Inventat în browser, e adevărat doar cât ține pagina
 *  deschisă, și numai pentru cine se uită la ea.
 *
 *  Stă totuși aici, acum, fiindcă documentele curente NU au obiecte de mobilier,
 *  iar o machetă cu camere goale nu e ce s-a cerut. Când etapa le va emite,
 *  fișierul se șterge întreg și `arh3d.scena3D` citește piesele din document —
 *  o singură funcție de înlocuit, `mobilierCamerei`.
 *
 *  Cotele sunt aceleași cu ale simbolurilor din `arhMobilier.tsx`: pat dublu
 *  1,62 × 2,05, canapea 2,25 × 0,92, blat 0,60, cadă 1,72 × 0,76. Amândouă
 *  desenele trebuie să spună același lucru despre aceeași cameră, altfel planul
 *  și macheta se contrazic. Și aceeași regulă: piesa care NU încape lipsește,
 *  nu se micșorează.
 *  ==========================================================================
 */
import type { Gol3, Pt } from "./arh3d";

/** O piesă așezată: modelul CC0, unde stă, în ce gabarit intră, cât e rotită. */
export interface Piesa3 {
  /** Numele modelului din `public/arh3d/mobilier/<m>.glb`. */
  m: string;
  p: [number, number, number];
  /** Gabaritul cerut, în metri — modelul se scalează ca să intre în el. */
  d: [number, number];
  /** Rotirea, în grade. */
  r: number;
  /** OGLINDIREA, pe axa lungă a piesei și pe cea scurtă, după rotire. Numai
   *  mobilierul pus de mână o poate avea — cel dedus din felul camerei își
   *  alege singur sensul. Machetei îi ajunge un semn, fiindcă oglindirea e o
   *  scară negativă în matricea instanței. */
  ogl?: [boolean, boolean];
}

/** Cât spațiu liber cere o ușă în fața ei, și marginea laterală. */
const DEGAJARE = 0.90;
const MARGINE = 0.15;

// ---------------------------------------------------------------- cadrul camerei

/** Cadrul propriu al camerei: centrul, latura lungă pe x, unghiul în grade.
 *
 *  Centrul se întoarce în lume cu unghiul DE DINAINTE de rotire — `cu`/`cv` au
 *  fost măsurate în cadrul vechi. Recalculat din unghiul deja rotit, o cameră
 *  mai înaltă decât lată primea un centru la zeci de metri distanță, și de acolo
 *  mobilier care plutea lângă bloc. */
export function cadru(P: readonly Pt[]): [number, number, number, number, number] | null {
  if (P.length < 3) return null;
  let best = -1, a = 0;
  for (let i = 0; i < P.length; i++) {
    const [x0, y0] = P[i];
    const [x1, y1] = P[(i + 1) % P.length];
    const d = Math.hypot(x1 - x0, y1 - y0);
    if (d > best) { best = d; a = Math.atan2(y1 - y0, x1 - x0); }
  }
  const c = Math.cos(a), s = Math.sin(a);
  const us = P.map((p) => p[0] * c + p[1] * s);
  const vs = P.map((p) => -p[0] * s + p[1] * c);
  const cu = (Math.min(...us) + Math.max(...us)) / 2;
  const cv = (Math.min(...vs) + Math.max(...vs)) / 2;
  let w = Math.max(...us) - Math.min(...us);
  let h = Math.max(...vs) - Math.min(...vs);
  if (h > w) { const t = w; w = h; h = t; a += Math.PI / 2; }
  return [cu * c - cv * s, cu * s + cv * c, w, h, (a * 180) / Math.PI];
}

/** Punctul e în inel? Raycast clasic, pentru a decide încotro se deschide o ușă. */
function inauntru(p: Pt, P: readonly Pt[]): boolean {
  let c = false;
  for (let i = 0; i < P.length; i++) {
    const [x0, y0] = P[i];
    const [x1, y1] = P[(i + 1) % P.length];
    if ((y0 > p[1]) !== (y1 > p[1])
        && p[0] < ((x1 - x0) * (p[1] - y0)) / (y1 - y0) + x0) c = !c;
  }
  return c;
}

/** Golurile care dau ÎN camera asta, fiecare cu normala spre interior. */
function usileCamerei(P: readonly Pt[], goluri: readonly Gol3[]) {
  const out: { a: Pt; b: Pt; n: Pt }[] = [];
  for (const g of goluri) {
    const m: Pt = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
    let best = 1e9;
    for (let i = 0; i < P.length; i++) {
      const a = P[i], b = P[(i + 1) % P.length];
      const vx = b[0] - a[0], vy = b[1] - a[1];
      const L2 = vx * vx + vy * vy;
      const t = L2 < 1e-12 ? 0
        : Math.max(0, Math.min(1, ((m[0] - a[0]) * vx + (m[1] - a[1]) * vy) / L2));
      best = Math.min(best, Math.hypot(m[0] - (a[0] + vx * t), m[1] - (a[1] + vy * t)));
    }
    if (best >= 0.35) continue;
    const dx = g.b[0] - g.a[0], dy = g.b[1] - g.a[1];
    const L = Math.hypot(dx, dy);
    if (L < 1e-9) continue;
    let n: Pt = [-dy / L, dx / L];
    if (!inauntru([m[0] + n[0] * 0.3, m[1] + n[1] * 0.3], P)) n = [-n[0], -n[1]];
    out.push({ a: g.a, b: g.b, n });
  }
  return out;
}

/** Cadrul, minus spațiul pe care fiecare ușă îl cere liber.
 *
 *  O ușă se poate ocoli în ADÂNCIME (te dai înapoi din fața ei) sau LATERAL (te
 *  dai într-o parte); care din două lasă mai mult loc depinde de unde cade ușa,
 *  deci se încearcă amândouă pentru fiecare ușă și se ține combinația cu aria
 *  cea mai mare. Peste zece uși într-o cameră combinațiile explodează, și tot
 *  atâtea uși într-o cameră înseamnă oricum altceva decât o cameră. */
function cadruLiber(c0: [number, number, number, number, number],
                    usi: readonly { a: Pt; b: Pt; n: Pt }[]):
    [number, number, number, number, number] | null {
  const [cx, cy, w, h, deg] = c0;
  const a = (deg * Math.PI) / 180, c = Math.cos(a), si = Math.sin(a);
  type Taiere = ["u0" | "u1" | "v0" | "v1", number];
  const taieri: [Taiere, Taiere][] = [];
  for (const u of usi.slice(0, 10)) {
    const dx = (u.a[0] + u.b[0]) / 2 - cx, dy = (u.a[1] + u.b[1]) / 2 - cy;
    const lu = dx * c + dy * si, lv = -dx * si + dy * c;
    const nu = u.n[0] * c + u.n[1] * si, nv = -u.n[0] * si + u.n[1] * c;
    const sp = Math.hypot(u.b[0] - u.a[0], u.b[1] - u.a[1]) / 2 + MARGINE;
    let adanc: Taiere, later: Taiere;
    if (Math.abs(nu) > Math.abs(nv)) {
      adanc = nu > 0 ? ["u0", lu + DEGAJARE] : ["u1", lu - DEGAJARE];
      later = lv < 0 ? ["v0", lv + sp] : ["v1", lv - sp];
    } else {
      adanc = nv > 0 ? ["v0", lv + DEGAJARE] : ["v1", lv - DEGAJARE];
      later = lu < 0 ? ["u0", lu + sp] : ["u1", lu - sp];
    }
    taieri.push([adanc, later]);
  }
  let best: [number, number, number, number, number] | null = null;
  for (let masca = 0; masca < (1 << taieri.length); masca++) {
    let u0 = -w / 2, u1 = w / 2, v0 = -h / 2, v1 = h / 2;
    for (let k = 0; k < taieri.length; k++) {
      const [lat, val] = taieri[k][(masca >> k) & 1];
      if (lat === "u0") u0 = Math.max(u0, val);
      else if (lat === "u1") u1 = Math.min(u1, val);
      else if (lat === "v0") v0 = Math.max(v0, val);
      else v1 = Math.min(v1, val);
    }
    const nw = u1 - u0, nh = v1 - v0;
    if (nw < 0.7 || nh < 0.7) continue;
    if (!best || nw * nh > best[0]) best = [nw * nh, u0, u1, v0, v1];
  }
  if (!best) return null;
  const [, u0, u1, v0, v1] = best;
  const nw = u1 - u0, nh = v1 - v0;
  const mu = (u0 + u1) / 2, mv = (v0 + v1) / 2;
  const ncx = cx + mu * c - mv * si, ncy = cy + mu * si + mv * c;
  return nh > nw ? [ncx, ncy, nh, nw, deg + 90] : [ncx, ncy, nw, nh, deg];
}

// ---------------------------------------------------------------- catalogul

/** O piesă în cadrul camerei: decalaj, cotă, gabarit, rotire, model. */
type Asezare = [number, number, number, number, number, number, string];

/** Ce mobilier cere o cameră de tipul ăsta, la mărimea asta.
 *
 *  Regula peste tot: piesa care nu încape LIPSEȘTE — nu se micșorează. Un pat
 *  dublu strâns la 1,20 m nu mai e un pat dublu, e o minciună despre cameră. */
function catalog(kind: string, w: number, h: number): Asezare[] {
  const o: Asezare[] = [];
  const P = (...a: Asezare) => o.push(a);
  if (kind === "bedroom" || kind === "dormitor") {
    const dublu = w >= 2.6;
    const pw = dublu ? 1.62 : 1.0;
    if (h < 2.25 || w < pw + 0.2) return o;
    const bx = -w / 2 + pw / 2 + 0.1;
    P(bx, 0, 0, pw, 2.05, 90, dublu ? "bedDouble" : "bedSingle");
    P(bx, 0.15, 0, pw + 1.0, 2.6, 90, "rugRectangle");
    if (w >= pw + 0.7) P(bx + pw / 2 + 0.28, -0.85, 0, 0.44, 0.42, 90, "cabinetBedDrawer");
    if (dublu && w >= pw + 1.3) P(bx - pw / 2 - 0.28, -0.85, 0, 0.44, 0.42, 270, "cabinetBedDrawer");
    if (w >= pw + 1.45) P(w / 2 - 0.3, 0.15, 0, 0.58, Math.min(1.9, h - 0.4), 90, "bookcaseClosedWide");
  } else if (kind === "living") {
    const cw = Math.min(2.25, w - 0.5);
    if (cw < 1.5 || h < 2.5) return o;
    const jos = h / 2 - 0.5;
    P(0, jos, 0, cw, 0.92, 180, "loungeSofa");
    P(0, jos - 1.15, 0, cw + 0.7, 2.1, 0, "rugRectangle");
    P(0, jos - 1.1, 0, 1.05, 0.62, 0, "tableCoffee");
    if (h > 3.3) {
      P(0, -h / 2 + 0.28, 0, cw * 0.72, 0.42, 0, "cabinetTelevision");
      P(0, -h / 2 + 0.34, 0.5, 1.05, 0.1, 0, "televisionModern");
    }
    if (w > 3.7) P(cw / 2 + 0.6, jos - 0.95, 0, 0.78, 0.8, 135, "loungeChair");
    if (w > 3.2 && h > 3.2) P(-cw / 2 - 0.42, -h / 2 + 0.55, 0, 0.46, 0.46, 0, "pottedPlant");
    if (w * h >= 20) {
      const mx = Math.min(w / 2 - 1.1, w / 4), my = -h / 2 + 1.2;
      P(mx, my, 0, 1.45, 0.9, 0, "table");
      for (const k of [-0.42, 0.42]) {
        P(mx + k, my - 0.62, 0, 0.44, 0.44, 0, "chair");
        P(mx + k, my + 0.62, 0, 0.44, 0.44, 180, "chair");
      }
    }
  } else if (kind === "kitchen") {
    const bl = Math.min(w - 0.3, 3.4);
    if (bl < 1.2 || h < 1.8) return o;
    const y = -h / 2 + 0.32, x = -bl / 2, pas = 0.6;
    const n = Math.floor(bl / pas);
    for (let i = 0; i < n; i++) {
      let model = "kitchenCabinet";
      if (i === 1) model = "kitchenSink";
      else if (i === n - 2 && n >= 4) model = "kitchenStove";
      else if (i % 3 === 2) model = "kitchenCabinetDrawer";
      P(x + pas * (i + 0.5), y, 0, pas, 0.62, 0, model);
    }
    if (bl > 2.2) P(x + bl - 0.34, y + 0.72, 0, 0.68, 0.68, 0, "kitchenFridge");
  } else if (kind === "bath" || kind === "bath2" || kind === "shower") {
    if (w < 1.1 || h < 0.9) return o;
    const sus = -h / 2 + 0.42;
    if (w >= 1.9) P(-w / 2 + 0.92, sus, 0, 1.72, 0.76, 0, "bathtub");
    else P(-w / 2 + 0.52, sus + 0.08, 0, 0.92, 0.92, 0, "showerRound");
    if (w > 1.95) P(w / 2 - 0.34, sus - 0.02, 0, 0.6, 0.48, 0, "bathroomSink");
    if (h >= 1.55) P(w / 2 - 0.3, h / 2 - 0.44, 0, 0.4, 0.62, 180, "toilet");
  } else if (kind === "wc") {
    if (w < 0.75 || h < 1.0) return o;
    P(0, -h / 2 + 0.42, 0, 0.4, 0.62, 0, "toiletSquare");
    if (h > 1.7) P(0, h / 2 - 0.32, 0, 0.46, 0.36, 180, "bathroomSink");
  } else if (kind === "balcony") {
    if (w < 1.5) return o;
    P(-w / 4, 0, 0, 0.72, 0.72, 0, "table");
    P(-w / 4 - 0.6, 0, 0, 0.42, 0.42, 90, "chair");
    P(-w / 4 + 0.6, 0, 0, 0.42, 0.42, 270, "chair");
    if (w > 2.4) P(w / 2 - 0.45, h / 2 - 0.3, 0, 0.5, 0.5, 0, "pottedPlant");
  } else if (kind === "store") {
    if (w < 0.75 || h < 0.6) return o;
    P(0, -h / 2 + 0.22, 0, Math.min(w - 0.15, 1.1), 0.36, 0, "bookcaseOpen");
  }
  return o;
}

/** Camerele care primesc mobilier. Un hol nu — și nici casa scării: acolo se
 *  desenează rampa, care e altceva decât o piesă de mobilă. */
export const MOBILATE_3D = new Set(["living", "bedroom", "dormitor", "kitchen",
                                    "bath", "bath2", "shower", "wc", "balcony",
                                    "store"]);

/** Piesele unei camere, așezate în lume.
 *
 *  ASTA e funcția care dispare când documentul aduce mobilierul: `scena3D` o va
 *  înlocui cu o citire a obiectelor lui, cu aceeași semnătură de ieșire. */
export function mobilierCamerei(kind: string, inel: readonly Pt[],
                                goluri: readonly Gol3[]): Piesa3[] {
  if (!MOBILATE_3D.has(kind)) return [];
  const c0 = cadru(inel);
  if (!c0) return [];
  const cl = cadruLiber(c0, usileCamerei(inel, goluri));
  if (!cl) return [];
  const ang = (cl[4] * Math.PI) / 180;
  const co = Math.cos(ang), si = Math.sin(ang);
  return catalog(kind, cl[2], cl[3]).map(([dx, dy, z, pw, pd, rot, model]) => ({
    m: model,
    p: [cl[0] + dx * co - dy * si, cl[1] + dx * si + dy * co, z] as [number, number, number],
    d: [pw, pd] as [number, number],
    r: cl[4] + rot,
  }));
}
